// Match replay (contracts/phase4.md "Admin": GET /arena/matches/:id). Round by round: the 10×10 dot
// field (who was in, who got cut, who's already out), the question, the answer-time distribution with
// the cut line and the 250 ms flag line, the cuts and the flagged answers. ← / → step through rounds,
// Home / End jump to the first and last; the round is kept in ?round= so a link points at it.

import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useArenaMatch } from "../api/queries";
import type { ArenaAnswer, ArenaMatch, ArenaPlacement } from "../api/schemas";
import { Orb } from "../components/Orb";
import { tableStyles } from "../components/table";
import { Button, Card, Chip, EmptyState, ErrorState, Loading, PageHeader, Stat } from "../components/ui";
import { answerHistogram, buildReplay, FLAG_MS, fmtSeconds, operationLabel, REASON_LABEL, seatState, type Replay, type ReplayRound } from "../lib/arena";
import { formatDateTime } from "../lib/dates";
import { fmtInt, fmtMs, shortId } from "../lib/format";
import { useHotkey } from "../lib/hotkeys";
import a from "./arena.module.css";
import { matchLabel } from "./ArenaPage";
import styles from "./pages.module.css";

const REASON_TONE: Record<ArenaPlacement["reason"], string> = {
  winner: "accent",
  final: "violet",
  cut: "muted",
  forfeit: "warning",
  excluded: "danger",
  aborted: "danger",
};

export function ArenaMatchPage() {
  const { id = "" } = useParams();
  const q = useArenaMatch(id);
  const header = (
    <PageHeader
      title={q.data ? <span className={a.title}>Match {matchLabel(q.data.matchId)}</span> : <span className="mono">{shortId(id, 28)}</span>}
      meta={
        <>
          <Link to={q.data ? `/arena?from=${q.data.startedAt?.slice(0, 10) ?? ""}&to=${q.data.startedAt?.slice(0, 10) ?? ""}` : "/arena"} className={styles.back}>
            ← Arena
          </Link>
          {q.data ? (
            <>
              {q.data.dropId ? <span className="mono">{q.data.dropId}</span> : null}
              {q.data.dev ? <Chip tone="violet">dev</Chip> : null}
              {q.data.aborted ? <Chip tone="danger">{`aborted${q.data.abortReason ? ` · ${q.data.abortReason}` : ""}`}</Chip> : null}
              {q.data.startedAt ? <span className="mono">{formatDateTime(q.data.startedAt)}</span> : null}
            </>
          ) : null}
        </>
      }
    />
  );
  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={12} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} title={(q.error as { status?: number }).status === 404 ? "No such match" : "Couldn't load this match"} />
      </>
    );
  return (
    <>
      {header}
      <MatchReplay match={q.data} />
    </>
  );
}

function MatchReplay({ match }: { match: ArenaMatch }) {
  const replay = useMemo(() => buildReplay(match), [match]);
  const [params, setParams] = useSearchParams();
  const count = replay.rounds.length;
  const raw = Number(params.get("round"));
  const index = Number.isInteger(raw) && raw >= 1 && raw <= count ? raw - 1 : 0;
  const go = (i: number) => {
    const next = Math.max(0, Math.min(count - 1, i));
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("round", String(next + 1));
        return p;
      },
      { replace: true },
    );
  };
  useHotkey("ArrowRight", (e) => {
    e.preventDefault();
    go(index + 1);
  });
  useHotkey("ArrowLeft", (e) => {
    e.preventDefault();
    go(index - 1);
  });
  useHotkey("Home", () => go(0));
  useHotkey("End", () => go(count - 1));
  const [selected, setSelected] = useState<number | null>(null);
  const r = replay.rounds[index];
  const humans = match.placements.filter((p) => !p.isBot);
  const bots = match.placements.length - humans.length;

  return (
    <>
      <div className={styles.statStrip} data-testid="match-summary">
        <Stat label="Players" value={fmtInt(match.players)} sub={bots ? `${bots} labelled BOT` : undefined} />
        <Stat label="Rounds" value={`${match.cutRounds ?? "—"} + final`} sub={match.finalPoints ? `${match.finalPoints} final points` : undefined} />
        <Stat
          label="Winner"
          value={match.winner ? <span className={a.winnerName}>{match.winner.name}</span> : "—"}
          sub={match.winner ? `seat ${match.winner.seat ?? "—"}${match.winner.isBot ? " · BOT" : ""}` : match.aborted ? "aborted: no crown" : undefined}
        />
        <Stat label="Median RTT" value={match.medianRttMs === null ? "—" : `${Math.round(match.medianRttMs)} ms`} />
        <Stat label="Flagged answers" value={fmtInt(match.flaggedAnswers)} sub={`under ${FLAG_MS} ms`} />
        <Stat
          label="Length"
          value={match.startedAt && match.endedAt ? fmtMs(Date.parse(match.endedAt) - Date.parse(match.startedAt)) : "—"}
          sub={match.endedAt ? `ended ${formatDateTime(match.endedAt).slice(-9)}` : undefined}
        />
      </div>

      {!r ? (
        <EmptyState title="No rounds recorded">{match.aborted ? "The match was aborted before its first round closed." : "This match has no round data."}</EmptyState>
      ) : (
        <>
          <RoundStrip replay={replay} index={index} onPick={go} />
          <div className={a.replay}>
            <Card
              title={<span data-testid="round-label">{r.label}</span>}
              actions={
                <span className={a.stepper}>
                  <Button size="sm" variant="ghost" onClick={() => go(index - 1)} disabled={index === 0} aria-label="Previous round" shortcut="←">
                    Prev
                  </Button>
                  <span className="monoLabel" aria-live="polite">
                    {index + 1} / {count}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => go(index + 1)} disabled={index === count - 1} aria-label="Next round" shortcut="→">
                    Next
                  </Button>
                </span>
              }
            >
              <DotField replay={replay} round={r} match={match} selected={selected} onSelect={setSelected} />
              <FieldLegend round={r} />
              {selected !== null ? <SeatPanel seat={selected} replay={replay} round={r} onClose={() => setSelected(null)} /> : null}
            </Card>

            <div className={a.roundSide}>
              <Card title="Question">
                <div className={a.question} data-testid="round-question">
                  {r.round.question || "—"}
                </div>
                <div className={a.questionMeta}>
                  <Chip tone="arena">{operationLabel(r.round.operation)}</Chip>
                  {r.round.difficulty !== null ? <span className="monoLabel">difficulty {r.round.difficulty}</span> : null}
                  {r.durationMs !== null ? <span className="monoLabel">open {fmtSeconds(r.durationMs)}</span> : null}
                </div>
                <div className={styles.statsRow} style={{ marginTop: 16 }}>
                  <Stat label="In play" value={fmtInt(r.aliveBefore.size)} />
                  <Stat label={r.round.final ? "Point" : "Through"} value={r.round.final ? pointText(r, replay, match) : fmtInt(r.aliveAfter.size)} />
                  <Stat label="Cut" value={fmtInt(r.cut.size)} />
                  <Stat label="Correct" value={fmtInt(r.correct)} sub={`${r.wrong} wrong · ${r.missing.length} missing`} />
                  <Stat label="Median" value={fmtSeconds(r.medianMs)} sub="correct answers" />
                </div>
              </Card>
              <Card title="Answer times" actions={<span className="monoLabel">{r.answers.length} answers</span>}>
                <AnswerHistogram round={r} />
              </Card>
            </div>
          </div>

          <div className={a.roundTables}>
            <Card title={r.round.final ? "Point" : "Cut this round"} padded={false} actions={<Chip tone="muted">{String(r.cut.size)}</Chip>}>
              <CutTable replay={replay} round={r} />
            </Card>
            <Card title="Flagged answers this round" padded={false} actions={<Chip tone={r.flagged.length ? "warning" : "muted"}>{String(r.flagged.length)}</Chip>}>
              <div className={a.tableScroll}>
                <FlaggedTable replay={replay} answers={r.flagged} />
              </div>
            </Card>
          </div>
        </>
      )}

      <Card title="Placements" padded={false} actions={<Chip tone="muted">{String(match.placements.length)}</Chip>} className={a.placementsCard}>
        <Placements match={match} onSelect={(seat) => setSelected(seat)} />
      </Card>
    </>
  );
}

function pointText(r: ReplayRound, replay: Replay, match: ArenaMatch) {
  const w = r.round.pointWinnerSeat;
  if (w === null) return r.index === replay.rounds.length - 1 && match.winner ? "decided" : "replayed";
  return replay.bySeat.get(w)?.name ?? `seat ${w}`;
}

function RoundStrip({ replay, index, onPick }: { replay: Replay; index: number; onPick: (i: number) => void }) {
  return (
    <nav className={a.strip} aria-label="Rounds">
      {replay.rounds.map((r, i) => {
        const finalNo = replay.rounds.slice(0, i + 1).filter((x) => x.round.final).length;
        return (
        <button
          key={r.round.round}
          type="button"
          className={a.stripItem}
          aria-current={i === index ? "step" : undefined}
          data-final={r.round.final || undefined}
          data-flagged={r.flagged.length > 0 || undefined}
          onClick={() => onPick(i)}
          title={`${r.label}: ${r.round.question}`}
        >
          <span className={a.stripLabel}>{r.round.final ? `F${finalNo}` : `R${r.round.displayRound ?? r.round.round}`}</span>
          <span className={a.stripBar} aria-hidden>
            <span style={{ width: `${r.aliveBefore.size ? (r.aliveAfter.size / r.aliveBefore.size) * 100 : 0}%` }} />
          </span>
          <span className={a.stripMeta}>{r.round.final ? (r.round.pointWinnerSeat === null ? "replay" : "point") : `${r.aliveBefore.size}→${r.aliveAfter.size}`}</span>
        </button>
        );
      })}
    </nav>
  );
}

const DOT_TITLE: Record<string, string> = { through: "through", cut: "cut this round", out: "already out", winner: "winner", empty: "empty seat" };

/** The board's 10×10 field (F2/F4/F7): violet in play, ember cut this round, dim already out, amber ring flagged. */
function DotField({ replay, round, match, selected, onSelect }: { replay: Replay; round: ReplayRound; match: ArenaMatch; selected: number | null; onSelect: (seat: number | null) => void }) {
  const cols = 10;
  const slots = Math.max(100, Math.ceil(replay.seats.length / cols) * cols);
  const rows = slots / cols;
  const pitch = 26;
  const r = 8.5;
  const answers = new Map(round.answers.map((x) => [x.seat, x]));
  const winnerSeat = match.winner?.seat ?? null;
  return (
    <svg className={a.field} viewBox={`0 0 ${cols * pitch} ${rows * pitch}`} role="img" aria-label={`${round.label}: ${round.aliveBefore.size} in play, ${round.cut.size} cut, ${round.aliveAfter.size} through`} data-testid="dot-field">
      {Array.from({ length: slots }, (_, i) => {
        const seat = replay.seats[i];
        const cx = (i % cols) * pitch + pitch / 2;
        const cy = Math.floor(i / cols) * pitch + pitch / 2;
        if (seat === undefined) return <circle key={`e${i}`} cx={cx} cy={cy} r={r - 1} className={a.dot} data-state="empty" />;
        const state = seatState(replay, round, seat, winnerSeat);
        const ans = answers.get(seat);
        const p = replay.bySeat.get(seat);
        return (
          <g key={seat} data-seat={seat} data-state={state} onClick={() => onSelect(selected === seat ? null : seat)} className={a.dotHit}>
            <title>{`Seat ${seat}${p ? ` · ${p.name}${p.isBot ? " (BOT)" : ""}` : ""} · ${DOT_TITLE[state]}${ans ? ` · ${ans.correct ? "correct" : "wrong"} in ${fmtSeconds(ans.answerMs)}${ans.flagged ? " · flagged" : ""}` : state === "out" ? "" : " · no answer"}`}</title>
            <circle cx={cx} cy={cy} r={r} className={a.dot} data-state={state} data-flagged={ans?.flagged || undefined} data-point={round.round.pointWinnerSeat === seat || undefined} />
            {selected === seat ? <circle cx={cx} cy={cy} r={r + 3.5} className={a.dotSelected} /> : null}
          </g>
        );
      })}
    </svg>
  );
}

function FieldLegend({ round }: { round: ReplayRound }) {
  const items = [
    { state: "through", label: round.round.final ? "finalist" : "through", n: round.aliveAfter.size },
    { state: "cut", label: "cut this round", n: round.cut.size },
    { state: "out", label: "already out", n: null },
  ];
  return (
    <div className={a.legend}>
      {items.map((x) => (
        <span key={x.state}>
          <i className={a.legendDot} data-state={x.state} />
          {x.label}
          {x.n !== null ? <span className="mono"> {x.n}</span> : null}
        </span>
      ))}
      <span>
        <i className={a.legendDot} data-state="through" data-flagged />
        flagged
      </span>
      <span className={a.legendHint}>Click a dot for the seat · ← → step rounds</span>
    </div>
  );
}

function SeatPanel({ seat, replay, round, onClose }: { seat: number; replay: Replay; round: ReplayRound; onClose: () => void }) {
  const p = replay.bySeat.get(seat);
  const ans = round.answers.find((x) => x.seat === seat);
  return (
    <div className={a.seatPanel} data-testid="seat-panel">
      <div className={a.seatHead}>
        {p?.orb !== null && p?.orb !== undefined ? <Orb index={p.orb} size={20} /> : null}
        <strong>{p?.name ?? `Seat ${seat}`}</strong>
        {p?.isBot ? <Chip tone="muted">BOT</Chip> : null}
        <span className="monoLabel">seat {seat}</span>
        {p ? <Chip tone={REASON_TONE[p.reason]}>{`${REASON_LABEL[p.reason]}${p.placement ? ` · #${p.placement}` : ""}`}</Chip> : null}
        <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close seat details">
          ✕
        </Button>
      </div>
      <p className={a.seatLine}>
        {!round.aliveBefore.has(seat)
          ? "Already out before this round."
          : ans
            ? `${ans.correct ? "Correct" : "Wrong"} in ${fmtSeconds(ans.answerMs)} (bucket ${fmtSeconds(ans.bucketMs)}, received +${ans.recvOffsetMs ?? "—"} ms, one-way ${ans.oneWayMs ?? "—"} ms, client ${ans.clientElapsedMs ?? "—"} ms), rank ${ans.rank ?? "—"}${ans.flagged ? " · flagged" : ""}.`
            : "No answer this round."}
        {round.cut.has(seat) ? " Cut when the round closed." : ""}
      </p>
      {p?.userId && !p.isBot ? (
        <Link to={`/players/${p.userId}`} className={styles.back}>
          Player detail →
        </Link>
      ) : null}
    </div>
  );
}

/** Answer times as stacked bars (correct, wrong), with the 250 ms flag line, the median and the cut line. */
function AnswerHistogram({ round }: { round: ReplayRound }) {
  const slowest = Math.max(0, ...round.answers.map((x) => x.answerMs));
  const binMs = slowest <= 4_000 ? 250 : slowest <= 10_000 ? 500 : 1_000;
  const bins = answerHistogram(round.answers, binMs);
  const W = 520;
  const H = 150;
  const padL = 26;
  const padB = 22;
  const plotW = W - padL - 6;
  const plotH = H - padB - 8;
  const max = Math.max(1, ...bins.map((b) => b.correct + b.wrong));
  const span = bins.length * binMs;
  const x = (ms: number) => padL + (ms / span) * plotW;
  const bw = plotW / bins.length;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round((t * span) / binMs) * binMs);
  if (!round.answers.length) return <EmptyState title="No answers">Nobody answered this round.</EmptyState>;
  return (
    <figure className={a.hist} data-testid="answer-histogram">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Answer times: ${round.correct} correct, ${round.wrong} wrong, median ${fmtSeconds(round.medianMs)}${round.cutLineMs !== null ? `, cut line ${fmtSeconds(round.cutLineMs)}` : ""}`}>
        <line x1={padL} x2={W - 6} y1={H - padB} y2={H - padB} className={a.axis} />
        {bins.map((b, i) => {
          const hc = (b.correct / max) * plotH;
          const hw = (b.wrong / max) * plotH;
          const x0 = padL + i * bw + 1;
          return (
            <g key={i}>
              <title>{`${fmtSeconds(b.from)}–${fmtSeconds(b.to)}: ${b.correct} correct, ${b.wrong} wrong${b.flagged ? `, ${b.flagged} flagged` : ""}`}</title>
              {hc ? <rect x={x0} y={H - padB - hc} width={Math.max(1, bw - 2)} height={hc} className={a.barCorrect} rx={1.5} /> : null}
              {hw ? <rect x={x0} y={H - padB - hc - hw} width={Math.max(1, bw - 2)} height={hw} className={a.barWrong} rx={1.5} /> : null}
            </g>
          );
        })}
        <line x1={x(FLAG_MS)} x2={x(FLAG_MS)} y1={6} y2={H - padB} className={a.flagLine} />
        <text x={x(FLAG_MS) + 4} y={14} className={a.lineLabel} data-kind="flag">
          {FLAG_MS} ms
        </text>
        {round.medianMs !== null ? <line x1={x(round.medianMs)} x2={x(round.medianMs)} y1={6} y2={H - padB} className={a.medianLine} /> : null}
        {round.cutLineMs !== null ? (
          <>
            <line x1={x(round.cutLineMs)} x2={x(round.cutLineMs)} y1={6} y2={H - padB} className={a.cutLine} />
            {/* The label sits on whichever side of the line has room. */}
            <text
              x={x(round.cutLineMs) > W * 0.62 ? x(round.cutLineMs) - 5 : x(round.cutLineMs) + 5}
              y={26}
              textAnchor={x(round.cutLineMs) > W * 0.62 ? "end" : "start"}
              className={a.lineLabel}
              data-kind="cut"
            >
              cut line {fmtSeconds(round.cutLineMs)}
            </text>
          </>
        ) : null}
        {ticks.map((t) => (
          <text key={t} x={x(t)} y={H - 6} className={a.tick} textAnchor={t === 0 ? "start" : t === span ? "end" : "middle"}>
            {t === 0 ? "0" : fmtSeconds(t)}
          </text>
        ))}
        <text x={padL - 6} y={H - padB - plotH + 8} className={a.tick} textAnchor="end">
          {max}
        </text>
      </svg>
      <figcaption className={a.histLegend}>
        <span>
          <i data-kind="correct" /> correct
        </span>
        <span>
          <i data-kind="wrong" /> wrong
        </span>
        <span>
          <i data-kind="median" /> median {fmtSeconds(round.medianMs)}
        </span>
        {round.cutLineMs !== null ? (
          <span>
            <i data-kind="cut" /> slowest correct that survived
          </span>
        ) : null}
        <span>
          <i data-kind="flag" /> flag threshold
        </span>
      </figcaption>
    </figure>
  );
}

function PlayerCell({ p, seat }: { p: ArenaPlacement | undefined; seat: number }) {
  if (!p) return <span className="mono">seat {seat}</span>;
  const body = (
    <>
      {p.orb !== null ? <Orb index={p.orb} size={16} /> : null}
      <span className={a.playerName}>{p.name || `seat ${seat}`}</span>
    </>
  );
  return (
    <span className={a.player}>
      {p.userId && !p.isBot ? (
        <Link to={`/players/${p.userId}`} className={a.player}>
          {body}
        </Link>
      ) : (
        body
      )}
      {p.isBot ? <Chip tone="muted">BOT</Chip> : null}
    </span>
  );
}

function CutTable({ replay, round }: { replay: Replay; round: ReplayRound }) {
  if (round.round.final) {
    const [x, y] = replay.finalists;
    return (
      <table className={tableStyles.table} data-testid="final-point">
        <thead>
          <tr>
            <th>Finalist</th>
            <th>Answer</th>
            <th data-align="right">Time</th>
            <th>Point</th>
          </tr>
        </thead>
        <tbody>
          {[x, y]
            .filter((s): s is number => s !== undefined)
            .map((s) => {
              const ans = round.answers.find((z) => z.seat === s);
              return (
                <tr key={s} data-seat={s}>
                  <td>
                    <PlayerCell p={replay.bySeat.get(s)} seat={s} />
                  </td>
                  <td>{ans ? <Chip tone={ans.correct ? "success" : "danger"}>{ans.correct ? "correct" : "wrong"}</Chip> : <span className={tableStyles.faint}>no answer</span>}</td>
                  <td data-align="right" className="mono">
                    {ans ? fmtSeconds(ans.answerMs) : "—"}
                  </td>
                  <td>{round.round.pointWinnerSeat === s ? <Chip tone="accent">won the point</Chip> : round.cut.has(s) ? <Chip tone="violet">runner-up</Chip> : null}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    );
  }
  if (!round.cut.size) return <EmptyState title="Nobody cut">Everyone in play went through.</EmptyState>;
  const answers = new Map(round.answers.map((x) => [x.seat, x]));
  const rows = [...round.cut].map((s) => ({ s, ans: answers.get(s) })).sort((p, q) => (p.ans?.rank ?? 1e9) - (q.ans?.rank ?? 1e9) || p.s - q.s);
  return (
    <div className={a.tableScroll}>
      <table className={tableStyles.table} data-testid="cut-table">
        <thead>
          <tr>
            <th>Player</th>
            <th data-align="right">Seat</th>
            <th>Answer</th>
            <th data-align="right">Time</th>
            <th data-align="right">Rank</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ s, ans }) => (
            <tr key={s} data-seat={s}>
              <td>
                <PlayerCell p={replay.bySeat.get(s)} seat={s} />
              </td>
              <td data-align="right" className="mono">
                {s}
              </td>
              <td>
                {ans ? (
                  <Chip tone={ans.correct ? "success" : "danger"}>{ans.correct ? "correct" : "wrong"}</Chip>
                ) : (
                  <span className={tableStyles.faint}>{replay.bySeat.get(s)?.excluded ? "excluded" : "no answer"}</span>
                )}
              </td>
              <td data-align="right" className="mono">
                {ans ? fmtSeconds(ans.answerMs) : "—"}
              </td>
              <td data-align="right" className="mono">
                {ans?.rank ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FlaggedTable({ replay, answers }: { replay: Replay; answers: ArenaAnswer[] }) {
  if (!answers.length) return <EmptyState title="No flags">No answer came in under {FLAG_MS} ms.</EmptyState>;
  return (
    <table className={tableStyles.table} data-testid="flagged-table">
      <thead>
        <tr>
          <th>Player</th>
          <th data-align="right">Answer</th>
          <th data-align="right">Received</th>
          <th data-align="right">One-way</th>
          <th data-align="right">Client</th>
          <th>Result</th>
        </tr>
      </thead>
      <tbody>
        {answers.map((x) => {
          const p = replay.bySeat.get(x.seat);
          return (
            <tr key={x.seat} data-seat={x.seat}>
              <td>
                <PlayerCell p={p} seat={x.seat} />
              </td>
              <td data-align="right" className="mono">
                {x.answerMs} ms
              </td>
              <td data-align="right" className="mono">
                {x.recvOffsetMs === null ? "—" : `+${x.recvOffsetMs} ms`}
              </td>
              <td data-align="right" className="mono">
                {x.oneWayMs === null ? "—" : `${x.oneWayMs} ms`}
              </td>
              <td data-align="right" className="mono">
                {x.clientElapsedMs === null ? "—" : `${x.clientElapsedMs} ms`}
              </td>
              <td>
                <span className={styles.chips}>
                  <Chip tone={x.correct ? "success" : "danger"}>{x.correct ? "correct" : "wrong"}</Chip>
                  {p?.excluded ? <Chip tone="danger">excluded</Chip> : null}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Placements({ match, onSelect }: { match: ArenaMatch; onSelect: (seat: number) => void }) {
  const [all, setAll] = useState(false);
  const rows = all ? match.placements : match.placements.slice(0, 20);
  return (
    <>
      <div className={a.tableScroll}>
        <table className={tableStyles.table} data-testid="placements">
          <thead>
            <tr>
              <th data-align="right">#</th>
              <th>Player</th>
              <th data-align="right">Seat</th>
              <th>Result</th>
              <th data-align="right">Out in</th>
              <th data-align="right">Answered</th>
              <th data-align="right">Correct</th>
              <th data-align="right">Avg</th>
              <th data-align="right">Fastest</th>
              <th data-align="right">Flags</th>
              <th data-align="right">XP</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.seat} data-seat={p.seat} data-clickable onClick={(e) => !(e.target as HTMLElement).closest("a") && onSelect(p.seat)}>
                <td data-align="right" className="mono">
                  {p.placement || "—"}
                </td>
                <td>
                  <PlayerCell p={p} seat={p.seat} />
                </td>
                <td data-align="right" className="mono">
                  {p.seat}
                </td>
                <td>
                  <span className={styles.chips}>
                    <Chip tone={REASON_TONE[p.reason]}>{REASON_LABEL[p.reason]}</Chip>
                    {p.reason === "winner" && !match.aborted ? <Chip tone="accent">crown</Chip> : null}
                  </span>
                </td>
                <td data-align="right" className="mono">
                  {p.eliminatedRound ? `R${p.eliminatedRound}` : "—"}
                </td>
                <td data-align="right" className="mono">
                  {p.answered}
                </td>
                <td data-align="right" className="mono">
                  {p.correct}
                </td>
                <td data-align="right" className="mono">
                  {p.avgMs ? fmtSeconds(p.avgMs) : "—"}
                </td>
                <td data-align="right" className="mono">
                  {p.fastestMs ? fmtSeconds(p.fastestMs) : "—"}
                </td>
                <td data-align="right">{p.flags ? <Chip tone={p.excluded ? "danger" : "warning"}>{String(p.flags)}</Chip> : <span className={tableStyles.faint}>0</span>}</td>
                <td data-align="right" className="mono">
                  {p.xp}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {match.placements.length > 20 ? (
        <div className={tableStyles.footer}>
          <span className="monoLabel">
            {rows.length} of {match.placements.length}
          </span>
          <Button size="sm" variant="ghost" onClick={() => setAll((v) => !v)}>
            {all ? "Show the top 20" : `Show all ${match.placements.length}`}
          </Button>
        </div>
      ) : null}
    </>
  );
}
